import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { LoadingState } from '@shared/types/loading-state';
import { Country } from '@shared/models/country';
import { loadCountries, loadCountriesSuccess, loadCountriesFailure } from '../actions/load-countries.actions';
import { selectCountry } from '../actions/select-country.actions';
import { TradingEntity } from '@shared/types/trading-entity';
import { selectSession } from '@shared/reducers/session.reducer';
import { GroupType } from '@shared/types/group-type';

export const featureKey = 'countries';

export interface State extends EntityState<Country> {
  loadingState: LoadingState;
  selectedCountryAlpha2Code?: string;
}

export const countryAdapter = createEntityAdapter<Country>({
  selectId: (country: Country) => country.attributes.alpha2_code,
  sortComparer: (a: Country, b: Country) => a.attributes.name.localeCompare(b.attributes.name),
});

export const initialState: State = countryAdapter.getInitialState({
  loadingState: 'not_loading',
});

export const countriesReducer = createReducer(
  initialState,
  on(loadCountries, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(loadCountriesSuccess, (state, { data }): State => {
    return countryAdapter.setAll(data, {
      ...state,
      loadingState: 'loaded' as LoadingState,
    });
  }),
  on(selectCountry, (state, { countryAlpha2Code }) => ({
    ...state,
    selectedCountryAlpha2Code: countryAlpha2Code,
  })),
  on(loadCountriesFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return countriesReducer(state, action);
}

export const countriesFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectAll, selectEntities } = countryAdapter.getSelectors(countriesFeatureSelector);

export const selectAllCountries = selectAll;

export const selectAvailableCountries = createSelector(selectAll, selectSession, (countries, session) =>
  countries.filter((country) =>
    country.attributes.trading_entities.some((tradingEntity) => session?.trading_entities?.includes(tradingEntity)),
  ),
);

export const selectCountriesLoadingState = createSelector(
  countriesFeatureSelector,
  (state: State) => state.loadingState,
);

export const selectCountriesByTradingEntityAndGroupType = (tradingEntity: TradingEntity, groupType: GroupType) =>
  createSelector(selectAllCountries, (countries) =>
    tradingEntity == '4DI_TECH' && groupType != 'customer'
      ? countries
      : countries.filter((country) => country.attributes.trading_entities.includes(tradingEntity)),
  );

export const selectSelectedCountry = createSelector(selectEntities, countriesFeatureSelector, (entities, state) => {
  const country = state.selectedCountryAlpha2Code == undefined ? undefined : entities[state.selectedCountryAlpha2Code];
  return !!country
    ? {
        ...country,
        attributes: {
          ...country.attributes,
          provinces: [...country.attributes.provinces].sort((a, b) => a.name.localeCompare(b.name)),
        },
      }
    : undefined;
});

export const selectCountryByAlpha2Code = (countryAlpha2Code: string) =>
  createSelector(selectEntities, (entities) => {
    const country = countryAlpha2Code == undefined ? undefined : entities[countryAlpha2Code];
    return !!country
      ? {
          ...country,
          attributes: {
            ...country.attributes,
            provinces: [...country.attributes.provinces].sort((a, b) => a.name.localeCompare(b.name)),
          },
        }
      : undefined;
  });
